import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { useAccount, useMsal } from '@azure/msal-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'

import { useAuthStore } from '@/store/auth-store'
import { ROUTES } from '@/router'
import { Role } from '@/types/auth'

const ROLE_OBJECT_KEY = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'

const getUserRoleFromToken = (token: string): Role => {
    const tokenPayload = token.split('.')[1]
    const user = JSON.parse(window.atob(tokenPayload))

    return user?.[ROLE_OBJECT_KEY] || Role.Controller
}

const getUserToken = async (msalIdToken: string) => {
    // get role from msal jwt token
    const tokenPayload = msalIdToken.split('.')[1]
    const user = JSON.parse(window.atob(tokenPayload))

    if (!user?.roles || !user?.roles?.length) {
        throw new Error('No roles found')
    }

    try {
        const res = await axios.post<string>(
            `${import.meta.env.VITE_API_AUTH_URL}/Token`,
            {},
            {
                headers: {
                    Authorization: `Bearer ${msalIdToken}`,
                },
            }
        )

        const token = res.data
        const role = getUserRoleFromToken(token)

        useAuthStore.getState().setAccessToken(res.data)
        useAuthStore.getState().setRole(role)
    } catch (error) {
        console.error(error)
        throw error
    }
}

export const useGetUserToken = () => {
    const { instance, accounts } = useMsal()
    const account = useAccount(accounts[0] || {})
    const { accessToken } = useAuthStore()
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState(false)

    const handleAccountChange = async () => {
        if (account) {
            const accessTokenRequest = {
                scopes: ['User.Read'],
                account,
            }
            try {
                setIsLoading(true)
                const msalCredentials = await instance.acquireTokenSilent(accessTokenRequest)
                await getUserToken(msalCredentials.idToken)
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError) {
                    instance.acquireTokenRedirect(accessTokenRequest).catch(function (error) {
                        // Acquire token interactive failure
                        console.error(error)
                        navigate(ROUTES.LOGIN)
                    })
                } else if (error instanceof Error && error.message === 'No roles found') {
                    // Handle the case where user has no roles
                    navigate(ROUTES.NO_PERMISSION)
                } else {
                    // Unhandled exception
                    console.error(error)
                    navigate(ROUTES.LOGIN)
                }
            } finally {
                setIsLoading(false)
            }
        }
    }

    useEffect(() => {
        if (!accessToken) {
            handleAccountChange()
        }
    }, [account])

    return {
        isLoading,
    }
}
